import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import i18n from "i18next";
import Alert from "../alert/Alert";
import { sanitizeThemeHtml } from "../../nav_bar/scene_theme_palette/sanitizeThemeHtml";
import "./_html_edit_modal.scss";

// Renders sanitized HTML into an isolated DOM subtree without raw HTML-sink
// assignments: DOMParser builds an off-document tree, we import the nodes
// into the live document, then append them via replaceChildren. Sanitizer
// runs here too as defence in depth.
export function HtmlPreviewPane({ html }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    const sanitized = sanitizeThemeHtml(html);
    const doc = new DOMParser().parseFromString(
      `<div id="__preview_root__">${sanitized}</div>`,
      "text/html"
    );
    const root = doc.getElementById("__preview_root__");
    const fragment = document.createDocumentFragment();
    if (root) {
      Array.from(root.childNodes).forEach((n) =>
        fragment.appendChild(document.importNode(n, true))
      );
    }
    ref.current.replaceChildren(fragment);
  }, [html]);
  return <div ref={ref} className="html-edit-preview" />;
}

HtmlPreviewPane.propTypes = {
  html: PropTypes.string.isRequired,
};

/**
 * Embeddable HTML editor (Edit/Preview tabs + textarea + sanitized preview).
 * Controlled component — caller owns the value.
 */
export function HtmlEditMode({ value, onChange, placeholder, rows = 10 }) {
  const [preview, setPreview] = useState(false);
  return (
    <div className="html-edit-mode">
      <div className="html-edit-toolbar" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={!preview}
          className={`html-edit-tab ${!preview ? "active" : ""}`}
          onClick={() => setPreview(false)}
        >
          {i18n.t("ThemePalette.Edit")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={preview}
          className={`html-edit-tab ${preview ? "active" : ""}`}
          onClick={() => setPreview(true)}
        >
          {i18n.t("ThemePalette.Preview")}
        </button>
      </div>
      {!preview ? (
        <textarea
          rows={rows}
          className="html-edit-textarea"
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <HtmlPreviewPane html={value} />
      )}
    </div>
  );
}

HtmlEditMode.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  rows: PropTypes.number,
};

/**
 * Full modal wrapper: Alert chrome around HtmlEditMode. Owns local html state
 * during the edit session; commits to onSave only when the user clicks Apply.
 */
export function HtmlEditModal({ show, onClose, initialHtml, onSave, title, placeholder }) {
  const [html, setHtml] = useState(initialHtml || "");

  useEffect(() => {
    if (show) {
      setHtml(initialHtml || "");
    }
  }, [show, initialHtml]);

  if (!show) return null;

  return (
    <Alert
      show={show}
      hasHeader
      title={title || i18n.t("EditButton.html_modal_title")}
      closeButton
      cancelButton
      acceptButtonText={i18n.t("ThemePalette.Apply")}
      onClose={(accepted) => {
        if (accepted) {
          onSave(sanitizeThemeHtml(html));
        }
        onClose(accepted);
      }}
      className="pageModal"
    >
      <HtmlEditMode
        value={html}
        onChange={setHtml}
        placeholder={placeholder || i18n.t("EditButton.html_placeholder")}
      />
    </Alert>
  );
}

HtmlEditModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initialHtml: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  title: PropTypes.string,
  placeholder: PropTypes.string,
};

export default HtmlEditModal;
